import React, { Fragment } from "react";
import { Popover, Transition } from "@headlessui/react";
import { MenuIcon, XIcon } from "@heroicons/react/outline";
import { Link } from "gatsby";
import logo from "../../images/logo.svg";
import { LocaleSelector, MobileLocaleSelector } from "./LocaleSelector";
import {
  blogPath,
  LANGUAGE_TO_APP_LOGIN,
  LANGUAGE_TO_HOMEPAGE,
  requestADemoPath,
} from "../../constants";
import { t, translator } from "../../translations";
import { classNames, SmartLink } from "../../utils";
import { ChevronDownIcon } from "@heroicons/react/solid";

const renderMenuItems = items =>
  items.map(item => (
    <Fragment key={item.name}>
      {item.children?.length > 0 ? (
        <Popover className="relative">
          {({ open }) => (
            <>
              <Popover.Button
                className={classNames(
                  open ? "text-gray-900" : "text-gray-500",
                  "group py-1 bg-white rounded-md inline-flex items-center text-base font-medium hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-action-500",
                )}
              >
                <span>{item.name}</span>
                <ChevronDownIcon
                  className={classNames(
                    open ? "text-gray-600" : "text-gray-400",
                    "ml-1 h-5 w-5 group-hover:text-gray-500",
                  )}
                  aria-hidden="true"
                />
              </Popover.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <Popover.Panel
                  className={classNames(
                    "absolute z-10 transform mt-3 w-screen",
                    item.children.length > 1
                      ? "-ml-4 max-w-md lg:max-w-3xl"
                      : "left-1/2 -translate-x-1/2 px-2 max-w-xs sm:px-0",
                  )}
                >
                  <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                    <div
                      className={
                        item.children.length > 1
                          ? `relative grid lg:grid-cols-${item.children.length}`
                          : ""
                      }
                    >
                      {item.children.map(itemChild => (
                        <div
                          key={itemChild.name}
                          className="relative grid gap-6 bg-white px-5 py-5 sm:gap-8 sm:p-8"
                        >
                          {item.children.length > 1 ? (
                            <>
                              <p className="text-sm font-semibold uppercase text-action-600 tracking-wider">
                                {itemChild.name}
                              </p>
                              {itemChild.children.map(itemGrandChild => (
                                <Link
                                  key={itemGrandChild.name}
                                  to={itemGrandChild.href}
                                >
                                  <Popover.Button className="-m-3 p-3 flex items-start rounded-lg hover:bg-gray-50 text-left">
                                    <img
                                      className="flex-shrink-0 h-6 w-6 text-action-600"
                                      aria-hidden="true"
                                      src={itemGrandChild.icon}
                                      alt={itemGrandChild.name}
                                    />
                                    <div className="ml-4">
                                      <p className="text-base font-medium text-gray-900">
                                        {itemGrandChild.name}
                                      </p>
                                      <p className="mt-1 text-sm text-gray-500">
                                        {itemGrandChild.description}
                                      </p>
                                    </div>
                                  </Popover.Button>
                                </Link>
                              ))}
                            </>
                          ) : (
                            itemChild.children.map(itemGrandChild => (
                              <SmartLink
                                key={itemGrandChild.name}
                                href={itemGrandChild.href}
                              >
                                <div className="-m-3 p-3 block rounded-md hover:bg-gray-50">
                                  <p className="text-base font-medium text-gray-900">
                                    {itemGrandChild.name}
                                  </p>
                                  {itemGrandChild.description && (
                                    <p className="mt-1 text-sm text-gray-500">
                                      {itemGrandChild.description}
                                    </p>
                                  )}
                                </div>
                              </SmartLink>
                            ))
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          )}
        </Popover>
      ) : (
        <div className="items-center">
          <Link
            to={item.href}
            className="text-base font-medium text-gray-500 hover:text-gray-900"
          >
            {item.name}
          </Link>
        </div>
      )}
    </Fragment>
  ));

const renderPopoverMenuItems = items =>
  items.flatMap(item =>
    item.children ? (
      renderPopoverMenuItems(item.children)
    ) : (
      <Link
        key={item.name}
        to={item.href}
        className="text-base font-medium text-gray-900 hover:text-gray-700"
      >
        {item.name}
      </Link>
    ),
  );

export const Navigation = ({
  locale,
  rolesMenuItem,
  otherMenuItems = [
    { name: t(locale, "navigation.news"), href: blogPath({ locale }) },
  ],
}) => {
  const t = translator(locale);

  return (
    <Popover className="z-50 w-full top-0">
      <div className="flex justify-between items-center px-4 py-3 sm:px-6 lg:justify-start lg:space-x-10 min-h-[62px]">
        <div>
          <Link to={LANGUAGE_TO_HOMEPAGE[locale] || "/"} className="flex">
            <span className="sr-only">Omnidoc</span>
            <img className="h-4 w-auto sm:h-4 mb-0" src={logo} alt="" />
          </Link>
        </div>
        <div className="-mr-2 -my-2 lg:hidden">
          <Popover.Button className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-action-500">
            <span className="sr-only">{t("navigation.open-menu")}</span>
            <MenuIcon className="h-6 w-6" aria-hidden="true" />
          </Popover.Button>
        </div>
        <div className="hidden lg:flex-1 lg:flex lg:items-center lg:justify-between">
          <Popover.Group
            as="nav"
            className="flex items-center gap-8"
          >
            {rolesMenuItem && renderMenuItems([rolesMenuItem])}
            {renderMenuItems(otherMenuItems)}
          </Popover.Group>
          <div className="flex items-center lg:ml-12">
            {requestADemoPath(locale) && (
              <Link
                to={requestADemoPath(locale)}
                className="text-base font-medium text-gray-500 hover:text-gray-900"
              >
                {t("navigation.request-a-demo")}
              </Link>
            )}
            {LANGUAGE_TO_APP_LOGIN[locale] && (
              <a
                href={LANGUAGE_TO_APP_LOGIN[locale]}
                className="ml-8 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-action-600 hover:bg-action-700"
              >
                {t("navigation.log-in")}
              </a>
            )}
            <LocaleSelector locale={locale} />
          </div>
        </div>
      </div>

      <Transition
        as={Fragment}
        enter="duration-200 ease-out"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="duration-100 ease-in"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Popover.Panel
          focus
          className="absolute z-50 top-0 inset-x-0 p-2 transition transform origin-top-right lg:hidden"
        >
          <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 bg-white divide-y-2 divide-gray-50 h-screen max-h-fit overflow-y-auto">
            <div className="pt-5 pb-6 px-5">
              <div className="flex items-center justify-between">
                <div>
                  <img className="h-5 w-auto sm:h-5 mb-0" src={logo} alt="" />
                </div>
                <div className="-mr-2">
                  <Popover.Button className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-action-500">
                    <span className="sr-only">
                      {t("navigation.close-menu")}
                    </span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </Popover.Button>
                </div>
              </div>
              {rolesMenuItem && (
                <div className="mt-6">
                  <nav className="grid gap-6">
                    {rolesMenuItem.children.map(itemChild => (
                      <Fragment key={itemChild.name}>
                        <p className="text-base font-semibold uppercase text-action-600 tracking-wider">
                          {itemChild.name}
                        </p>
                        {itemChild.children?.map(itemGrandChild => (
                          <Link
                            key={itemGrandChild.name}
                            to={itemGrandChild.href}
                            className="-m-3 p-3 flex items-start rounded-lg hover:bg-gray-50"
                          >
                            <img
                              className="flex-shrink-0 h-6 w-6 text-action-600"
                              aria-hidden="true"
                              src={itemGrandChild.icon}
                              alt={itemGrandChild.name}
                            />
                            <div className="ml-4 text-base font-medium text-gray-900">
                              {itemGrandChild.name}
                            </div>
                          </Link>
                        ))}
                      </Fragment>
                    ))}
                  </nav>
                </div>
              )}
            </div>
            <div className="py-6 px-5">
              <div className="grid grid-cols-2 gap-4">
                {renderPopoverMenuItems(otherMenuItems)}
              </div>
              <div className="mt-6">
                {LANGUAGE_TO_APP_LOGIN[locale] && (
                  <a
                    href={LANGUAGE_TO_APP_LOGIN[locale]}
                    className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-action-600 hover:bg-action-700"
                  >
                    {t("navigation.log-in")}
                  </a>
                )}
                {requestADemoPath(locale) && (
                  <p className="mt-6 text-center text-base font-medium text-gray-500">
                    {t("navigation.want-to-try-our-solutions")}{" "}
                    <Link
                      to={requestADemoPath(locale)}
                      className="text-action-600 hover:text-action-500"
                    >
                      {t("navigation.request-a-demo")}
                    </Link>
                  </p>
                )}
              </div>
              <MobileLocaleSelector locale={locale} />
            </div>
          </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  );
};
